import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'

const UI = {
  COLORS: {
    headerBg: '#f7f7f9',
    rowHover: 'rgba(0,89,246,0.03)',
    border: '#e7edf6',
    ink: '#06185f',
    muted: '#4c5d86',
    blue: '#0059f6',
  },
  WIDTHS: {
    fecha: 105,
    nombre: 170,
    telefono: 110,
    email: 170,
    origen: 120,
    consultas: 85,
    estado: 120,
    actividad: 115,
    archivado: 50,
  },
}

const STATUS_STYLES = {
  ACTIVO: { solid: '#2f8f3a', bg: 'rgba(47,143,58,0.12)', text: '#2f8f3a', label: 'Activo' },
  FUTURO: { solid: '#7d8495', bg: 'rgba(177,181,194,0.20)', text: '#6b7280', label: 'Futuro' },
  VENTA: { solid: '#0059f6', bg: 'rgba(0,89,246,0.10)', text: '#0059f6', label: 'Venta' },
  BAJA: { solid: '#e34d43', bg: 'rgba(253,141,130,0.18)', text: '#e34d43', label: 'Baja' },
  SIN_ASIGNAR: { solid: '#f26b00', bg: 'rgba(242,107,0,0.12)', text: '#c75a00', label: 'Sin asignar' },
  SIN_CONSULTAS: { solid: '#9aa3b2', bg: 'rgba(154,163,178,0.14)', text: '#6b7280', label: 'Sin consultas' },
}

const cellBase = {
  fontSize: '12px',
  borderColor: UI.COLORS.border,
  py: 1.25,
  px: 1,
  color: UI.COLORS.ink,
}

const headerCellBase = {
  ...cellBase,
  py: 1.35,
  fontWeight: 900,
  backgroundColor: UI.COLORS.headerBg,
}

function formatFecha(fechaStr) {
  if (!fechaStr) return '-'
  const d = new Date(fechaStr)
  if (Number.isNaN(d.getTime())) return '-'
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function formatPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '')
  if (!d) return '-'
  const a = d.slice(0, 3)
  const b = d.slice(3, 6)
  const c = d.slice(6, 10)
  return b ? `${a} ${b}${c ? ` ${c}` : ''}` : a
}

function statusStyle(status) {
  return STATUS_STYLES[status] || STATUS_STYLES.SIN_CONSULTAS
}

function EmptyState() {
  return (
    <TableRow>
      <TableCell colSpan={9} sx={{ py: 6, borderBottom: `1px solid ${UI.COLORS.border}` }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ opacity: 0.8 }}>
            No hay clientes para los filtros seleccionados
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.6 }}>
            Ajustá la búsqueda o el estado para ver otros resultados.
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  )
}

export default function ClientesTable({
  rows = [],
  count = 0,
  page = 0,
  rowsPerPage = 20,
  onPageChange,
  onOpenCliente,
  onArchiveChange,
  archiveUpdatingId,
  customHeight,
}) {
  const isFlexMode = customHeight === 'flex'

  return (
    <Box sx={{
      width: '100%',
      overflow: 'hidden',
      display: isFlexMode ? 'flex' : 'block',
      flexDirection: 'column',
      flex: isFlexMode ? 1 : 'none',
      height: isFlexMode ? '100%' : 'auto',
      minHeight: 0,
      backgroundColor: 'rgba(255,255,255,0.96)',
      border: '1px solid #dce5f2',
      borderRadius: '16px',
      boxShadow: '0 20px 44px rgba(19, 39, 84, 0.11)',
      boxSizing: 'border-box',
    }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: '16px 16px 8px 8px',
          border: `1px solid ${UI.COLORS.border}`,
          flex: isFlexMode ? 1 : 'none',
          height: isFlexMode ? '100%' : 'auto',
          minHeight: 0,
          overflowY: 'auto',
          maxHeight: isFlexMode ? 'none' : '56vh',
          backgroundColor: '#fff',
        }}
      >
        <Table size="small" aria-label="clientes" sx={{ tableLayout: 'fixed' }} stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { borderBottom: `1px solid ${UI.COLORS.border}` } }}>
              <TableCell sx={{ ...headerCellBase, width: UI.WIDTHS.fecha, borderRight: `1px solid ${UI.COLORS.border}` }}>Fecha carga</TableCell>
              <TableCell sx={{ ...headerCellBase, width: UI.WIDTHS.nombre, borderRight: `1px solid ${UI.COLORS.border}` }}>Nombre</TableCell>
              <TableCell sx={{ ...headerCellBase, width: UI.WIDTHS.telefono, borderRight: `1px solid ${UI.COLORS.border}` }}>Teléfono</TableCell>
              <TableCell sx={{ ...headerCellBase, width: UI.WIDTHS.email, borderRight: `1px solid ${UI.COLORS.border}` }}>Email</TableCell>
              <TableCell sx={{ ...headerCellBase, width: UI.WIDTHS.origen, borderRight: `1px solid ${UI.COLORS.border}` }}>Origen</TableCell>
              <TableCell align="center" sx={{ ...headerCellBase, width: UI.WIDTHS.consultas, borderRight: `1px solid ${UI.COLORS.border}` }}>Consultas</TableCell>
              <TableCell align="center" sx={{ ...headerCellBase, width: UI.WIDTHS.estado, borderRight: `1px solid ${UI.COLORS.border}` }}>Estado actual</TableCell>
              <TableCell align="center" sx={{ ...headerCellBase, width: UI.WIDTHS.actividad, borderRight: `1px solid ${UI.COLORS.border}` }}>Última actividad</TableCell>
              <TableCell align="center" sx={{ ...headerCellBase, width: UI.WIDTHS.archivado }}>
                <Tooltip title="Archivado" arrow>
                  <ArchiveOutlinedIcon sx={{ fontSize: 18, color: UI.COLORS.ink, verticalAlign: 'middle' }} />
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 && <EmptyState />}

            {rows.map((row) => {
              const style = statusStyle(row?.estado_actual)
              const vendedor = row?.vendedor_actual_nombre || ''
              const isArchived = Boolean(row?.archivado)
              const canArchive = Boolean(row?.archivable) || isArchived
              const archiveDisabled = !canArchive || archiveUpdatingId === row.id
              const archiveTitle = canArchive
                ? (isArchived ? 'Desarchivar cliente' : 'Archivar cliente')
                : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
                    <Box component="span">No se puede archivar:</Box>
                    <Box component="span">tiene consultas activas, futuras o sin asignar</Box>
                  </Box>
                )
              return (
                <TableRow
                  key={row.id}
                  hover
                  onDoubleClick={() => onOpenCliente?.(row)}
                  sx={{
                    cursor: 'default',
                    transition: 'background-color 120ms ease, opacity 120ms ease',
                    '&:hover': { backgroundColor: UI.COLORS.rowHover },
                    '& td': { borderBottom: `1px solid ${UI.COLORS.border}` },
                  }}
                >
                  <TableCell sx={cellBase}>{formatFecha(row?.fecha_carga)}</TableCell>
                  <TableCell sx={{ ...cellBase, fontWeight: 800 }} title={row?.nombre || '-'}>
                    <Button
                      variant="text"
                      onClick={() => onOpenCliente?.(row)}
                      sx={{
                        p: 0,
                        minWidth: 0,
                        maxWidth: '100%',
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 900,
                        color: UI.COLORS.blue,
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row?.nombre || '-'}
                      </Box>
                    </Button>
                  </TableCell>
                  <TableCell sx={cellBase}>{formatPhone(row?.telefono)}</TableCell>
                  <TableCell sx={cellBase} title={row?.email || 'Sin datos'}>
                    {row?.email || <Typography component="span" sx={{ color: '#9aa3b2', fontSize: 12 }}>Sin datos</Typography>}
                  </TableCell>
                  <TableCell sx={cellBase} title={row?.obtenido_de || 'Sin datos'}>
                    {row?.obtenido_de || <Typography component="span" sx={{ color: '#9aa3b2', fontSize: 12 }}>Sin datos</Typography>}
                  </TableCell>
                  <TableCell align="center" sx={cellBase}>
                    <Typography sx={{ fontSize: 12, fontWeight: 900 }}>{row?.consultas_count ?? 0}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={cellBase}>
                    <Tooltip title={vendedor ? `Vendedor actual: ${vendedor}` : ''} arrow disableHoverListener={!vendedor}>
                      <Chip
                        size="small"
                        label={style.label}
                        sx={{
                          height: 24,
                          color: style.text,
                          bgcolor: style.bg,
                          border: `1px solid ${style.solid}55`,
                          fontWeight: 900,
                          fontSize: 11,
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ ...cellBase, borderRight: `1px solid ${UI.COLORS.border}` }}>{formatFecha(row?.ultima_actividad)}</TableCell>
                  <TableCell align="center" sx={cellBase}>
                    <Tooltip title={archiveTitle} arrow>
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        <Checkbox
                          size="small"
                          checked={isArchived}
                          disabled={archiveDisabled}
                          onClick={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => e.stopPropagation()}
                          onChange={(e) => onArchiveChange?.(row, e.target.checked)}
                          inputProps={{ 'aria-label': isArchived ? 'Desarchivar cliente' : 'Archivar cliente' }}
                          sx={{
                            p: 0.25,
                            color: '#9aa3b2',
                            '&.Mui-checked': { color: UI.COLORS.blue },
                            '&.Mui-disabled': { color: '#c7ceda' },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        rowsPerPageOptions={[]}
        labelRowsPerPage=""
        labelDisplayedRows={({ from, to, count: totalCount }) => `${from}-${to} de ${totalCount}`}
        sx={{
          borderTop: `1px solid ${UI.COLORS.border}`,
          color: UI.COLORS.ink,
          '& .MuiTablePagination-toolbar': { minHeight: 46 },
          '& .MuiTablePagination-displayedRows': { fontSize: 12, fontWeight: 700 },
        }}
      />
    </Box>
  )
}
